<template>
  <div class="card detail-card">
    <div class="card-body">
      <div class="detail-grid">
        <!-- Store Info -->
        <div class="detail-section">
          <h6 class="section-title"><i class="fas fa-store mr-2"></i>店舗情報</h6>
          <!-- 店舗画像 -->
          <div class="shop-image-section">
            <img :src="data.shop_image || require('../../assets/img/default-150x150.png')" alt="店舗画像" class="shop-image-preview">
            <div class="shop-image-upload">
              <label class="btn btn-sm btn-outline-secondary">
                <i class="fas fa-camera mr-1"></i>画像変更
                <input type="file" accept="image/*" @change="uploadShopImage" style="display:none;">
              </label>
            </div>
          </div>
          <div v-if="!editing">
            <table class="table table-borderless detail-table">
              <tbody>
                <tr><th>店舗名</th><td>{{ data.shop_name }}</td></tr>
                <tr>
                  <th>管理者</th>
                  <td>
                    <span v-for="(s, i) in filteredStaff" :key="s.id">
                      {{ s.name }}<span v-if="i < filteredStaff.length - 1">、</span>
                    </span>
                  </td>
                </tr>
                <tr><th>スタッフ数</th><td>{{ data.number_of_staff }}名</td></tr>
                <tr><th>郵便番号</th><td>{{ data.postal_code }}</td></tr>
                <tr><th>住所</th><td>{{ data.address }}</td></tr>
                <tr><th>電話番号</th><td>{{ data.tel_number }}</td></tr>
                <tr><th>URL</th><td>{{ data.url }}</td></tr>
                <tr>
                  <th>営業時間</th>
                  <td>
                    <span v-if="data.opening_time && data.closing_time">
                      {{ formatTime(data.opening_time) }} 〜 {{ formatTime(data.closing_time) }}
                    </span>
                    <span v-else class="text-muted">未設定</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else>
            <div class="field-group">
              <label>店舗名</label>
              <input type="text" class="form-control" v-model="form.shop_name">
            </div>
            <div class="field-row">
              <div class="field-group">
                <label>郵便番号</label>
                <input type="text" class="form-control" v-model="form.postal_code">
              </div>
              <div class="field-group">
                <label>電話番号</label>
                <input type="text" class="form-control" v-model="form.tel_number">
              </div>
            </div>
            <div class="field-group">
              <label>住所</label>
              <textarea class="form-control" rows="2" v-model="form.address"></textarea>
            </div>
            <div class="field-group">
              <label>URL</label>
              <input type="text" class="form-control" v-model="form.url">
            </div>
            <div class="field-group">
              <label>営業時間</label>
              <div class="time-range">
                <input type="time" class="form-control" v-model="form.opening_time">
                <span class="range-sep">〜</span>
                <input type="time" class="form-control" v-model="form.closing_time">
              </div>
            </div>
          </div>
        </div>

        <!-- Shift Settings -->
        <div class="detail-section">
          <h6 class="section-title"><i class="fas fa-calendar-alt mr-2"></i>シフト設定</h6>
          <div v-if="!editing">
            <table class="table table-borderless detail-table">
              <tbody>
                <tr>
                  <th>シフトパターン</th>
                  <td>
                    <span v-if="data.shift_pattern_type === 1" class="badge-corporate">企業パターン</span>
                    <span v-else-if="data.shift_pattern_type === 2" class="badge-shift">シフトパターン</span>
                    <span v-else class="badge-corporate">企業パターン</span>
                  </td>
                </tr>
                <tr>
                  <th>タイムカード調整</th>
                  <td>
                    <span v-if="data.time_adjustment > 0" class="badge-on">{{ data.time_adjustment }}分単位</span>
                    <span v-else class="badge-off">行わない</span>
                  </td>
                </tr>
              </tbody>
            </table>
            <div class="pattern-description">
              <div v-if="data.shift_pattern_type !== 2" class="desc-card">
                <div class="desc-icon"><i class="fas fa-building"></i></div>
                <div>
                  <strong>企業パターン</strong>
                  <p>スタッフの勤務パターン・休日タイプ（土日祝/シフト制）に基づいてシフトを自動生成します。</p>
                </div>
              </div>
              <div v-else class="desc-card desc-shift">
                <div class="desc-icon"><i class="fas fa-utensils"></i></div>
                <div>
                  <strong>シフトパターン</strong>
                  <p>飲食店向け。時間帯ごとの必要人数など細かい条件を設定してシフトを生成します。</p>
                </div>
              </div>
            </div>
          </div>
          <div v-else>
            <div class="field-group">
              <label>シフトパターン</label>
              <div class="pattern-select">
                <button :class="['pattern-btn', { active: form.shift_pattern_type === 1 }]" @click="form.shift_pattern_type = 1">
                  <i class="fas fa-building mr-2"></i>
                  <div>
                    <strong>企業パターン</strong>
                    <small>土日祝/シフト制ベース</small>
                  </div>
                </button>
                <button :class="['pattern-btn', { active: form.shift_pattern_type === 2 }]" @click="form.shift_pattern_type = 2">
                  <i class="fas fa-utensils mr-2"></i>
                  <div>
                    <strong>シフトパターン</strong>
                    <small>飲食店向け細かい設定</small>
                  </div>
                </button>
              </div>
            </div>
            <div class="field-group mt-3">
              <label>タイムカード調整</label>
              <div class="time-adj-row">
                <label class="switch">
                  <input type="checkbox" v-model="timeAdjEnabled">
                  <span class="slider"></span>
                </label>
                <select v-if="timeAdjEnabled" class="form-control form-control-sm time-adj-select" v-model="form.time_adjustment">
                  <option :value="5">5分</option>
                  <option :value="10">10分</option>
                  <option :value="15">15分</option>
                  <option :value="30">30分</option>
                </select>
                <span v-else class="text-muted" style="font-size:13px;">無効</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Public Shift URL -->
      <div v-if="data.public_shift_token" style="margin-top: 16px;">
        <h6 class="section-title"><i class="fas fa-link mr-2"></i>シフト表 公開URL</h6>
        <div class="public-url-box">
          <input type="text" class="form-control" :value="publicShiftUrl" readonly ref="publicUrlInput">
          <button class="btn btn-outline-primary btn-sm" @click="copyPublicUrl"><i class="fas fa-copy mr-1"></i>コピー</button>
        </div>
        <p class="text-muted mt-2" style="font-size:12px;">
          <i class="fas fa-lock mr-1"></i>Basic認証で保護されています。外部スタッフへの共有にご利用ください。
        </p>
      </div>
    </div>
    <div class="card-footer detail-footer">
      <div v-if="!editing">
        <button class="btn btn-primary" @click="startEdit"><i class="fas fa-edit mr-1"></i>編集</button>
      </div>
      <div v-else class="edit-actions">
        <button class="btn btn-outline-secondary" @click="cancelEdit">キャンセル</button>
        <button class="btn btn-primary" @click="save"><i class="fas fa-save mr-1"></i>保存</button>
      </div>
    </div>
  </div>

  <!-- Shift Templates (separate card) -->
  <div v-if="(data.shift_pattern_type || 1) === 2" class="card mt-3">
    <div class="card-body">
      <div class="tmpl-header">
        <h6 class="section-title mb-0"><i class="fas fa-clock mr-2"></i>シフトテンプレート（時間帯パターン）</h6>
        <button class="btn btn-sm btn-success" @click="addTemplate"><i class="fas fa-plus mr-1"></i>追加</button>
      </div>
      <p class="text-muted mb-3" style="font-size:13px;">シフト自動生成で使用する時間帯パターンを設定します。</p>

      <div v-if="templates.length === 0" class="text-center text-muted py-3">テンプレートが登録されていません</div>

      <div v-for="(t, idx) in templates" :key="'tmpl-' + (t.id || idx)" class="tmpl-card">
        <div class="tmpl-row">
          <div class="tmpl-field" style="flex:1.5;">
            <label>名前</label>
            <input type="text" class="form-control form-control-sm" v-model="t.name" placeholder="例: 平日OP">
          </div>
          <div class="tmpl-field" style="flex:1;">
            <label>コード</label>
            <input type="text" class="form-control form-control-sm" v-model="t.code" placeholder="OP_WD">
          </div>
          <div class="tmpl-field">
            <label>開始</label>
            <input type="time" class="form-control form-control-sm" v-model="t.start_time">
          </div>
          <div class="tmpl-field">
            <label>終了</label>
            <input type="time" class="form-control form-control-sm" v-model="t.end_time">
          </div>
          <div class="tmpl-field" style="flex:1;">
            <label>日タイプ</label>
            <select class="form-control form-control-sm" v-model.number="t.day_type">
              <option :value="0">全日</option>
              <option :value="1">平日</option>
              <option :value="2">土曜</option>
              <option :value="3">日祝</option>
            </select>
          </div>
          <div class="tmpl-field tmpl-check">
            <label>クローズ</label>
            <input type="checkbox" v-model="t.is_close">
          </div>
          <button class="btn btn-sm btn-outline-danger tmpl-del" @click="removeTemplate(idx)"><i class="fas fa-times"></i></button>
        </div>
      </div>
    </div>
    <div class="card-footer detail-footer">
      <button class="btn btn-primary btn-sm" @click="saveTemplates"><i class="fas fa-save mr-1"></i>テンプレートを保存</button>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      data: { staff: [] },
      editing: false,
      form: {},
      timeAdjEnabled: false,
      templates: [],
    }
  },
  computed: {
    filteredStaff() {
      return (this.data.staff || []).filter(staff => staff.pivot && staff.pivot.manager_flag);
    },
    publicShiftUrl() {
      if (!this.data.public_shift_token) return '';
      const base = process.env.VUE_APP_API_PATH ? process.env.VUE_APP_API_PATH.replace(/\/api$/, '') : window.location.origin;
      return base + '/shared/shift/' + this.data.public_shift_token;
    },
  },
  mounted() {
    this.getShopDetails();
    this.loadTemplates();
  },
  methods: {
    getShopDetails() {
      const shop_id = this.$store.getters.shop;
      this.$http.get(process.env.VUE_APP_API_PATH + '/shop/shop/' + shop_id)
      .then(response => {
        this.data = response.data.data;
        this.data.number_of_staff = (this.data.staff || []).length;
      })
      .catch(error => { console.log(error); });
    },
    formatTime(t) {
      return t ? t.slice(0, 5) : '';
    },
    startEdit() {
      this.form = {
        shop_name: this.data.shop_name,
        postal_code: this.data.postal_code,
        address: this.data.address,
        tel_number: this.data.tel_number,
        url: this.data.url,
        opening_time: this.data.opening_time ? this.data.opening_time.slice(0, 5) : '',
        closing_time: this.data.closing_time ? this.data.closing_time.slice(0, 5) : '',
        time_adjustment: this.data.time_adjustment || 0,
        shift_pattern_type: this.data.shift_pattern_type || 1,
        managers: (this.data.staff || []).filter(s => s.pivot && s.pivot.manager_flag).map(s => s.id),
      };
      this.timeAdjEnabled = this.form.time_adjustment > 0;
      if (!this.timeAdjEnabled) this.form.time_adjustment = 15;
      this.editing = true;
    },
    cancelEdit() {
      this.editing = false;
    },
    // Templates
    loadTemplates() {
      const shop_id = this.$store.getters.shop;
      if (!shop_id) return;
      this.$http.get(process.env.VUE_APP_API_PATH + '/shop/shift-templates/' + shop_id)
      .then(res => {
        this.templates = (res.data.data || []).map(t => ({
          ...t,
          start_time: t.start_time ? t.start_time.slice(0, 5) : '',
          end_time: t.end_time ? t.end_time.slice(0, 5) : '',
        }));
      })
      .catch(() => {});
    },
    addTemplate() {
      this.templates.push({
        id: null, name: '', code: '', start_time: '09:00', end_time: '18:00',
        is_close: false, day_type: 0,
      });
    },
    removeTemplate(idx) {
      this.templates.splice(idx, 1);
    },
    saveTemplates() {
      const shop_id = this.$store.getters.shop;
      this.$http.post(process.env.VUE_APP_API_PATH + '/shop/shift-templates/' + shop_id + '/bulk', {
        templates: this.templates,
      })
      .then(res => {
        this.$toast.success('テンプレートを保存しました');
        this.templates = (res.data.data || []).map(t => ({
          ...t,
          start_time: t.start_time ? t.start_time.slice(0, 5) : '',
          end_time: t.end_time ? t.end_time.slice(0, 5) : '',
        }));
      })
      .catch(() => { this.$toast.error('保存に失敗しました'); });
    },
    uploadShopImage(event) {
      const file = event.target.files[0];
      if (!file) return;
      const shop_id = this.$store.getters.shop;
      const formData = new FormData();
      formData.append('file', file);
      formData.append('id', shop_id);
      this.$http.post(process.env.VUE_APP_API_PATH + '/company/updateShopImage', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      .then(response => {
        if (response.data.success) {
          this.$toast.success('店舗画像を更新しました');
          this.getShopDetails();
        }
      })
      .catch(() => { this.$toast.error('画像の更新に失敗しました'); });
    },
    copyPublicUrl() {
      const input = this.$refs.publicUrlInput;
      if (input) {
        input.select();
        navigator.clipboard.writeText(input.value).then(() => {
          this.$toast.success('URLをコピーしました');
        }).catch(() => {
          document.execCommand('copy');
          this.$toast.success('URLをコピーしました');
        });
      }
    },
    save() {
      const shop_id = this.$store.getters.shop;
      if (!this.timeAdjEnabled) this.form.time_adjustment = 0;
      this.$http.put(process.env.VUE_APP_API_PATH + '/shop/shop/' + shop_id, this.form)
      .then(response => {
        if (response.data.success) {
          this.$toast.success('店舗情報を更新しました');
          this.editing = false;
          this.getShopDetails();
        }
      })
      .catch(error => {
        if (error.response) {
          this.$toast.error(error.response.data?.message || 'エラーが発生しました');
        }
      });
    },
  },
};
</script>

<style scoped>
.detail-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
}

@media (max-width: 768px) {
  .detail-grid { grid-template-columns: 1fr; }
}

.section-title {
  font-size: 14px;
  font-weight: 700;
  color: #049C4C;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding-bottom: 10px;
  margin-bottom: 0;
  border-bottom: 2px solid #e2e8f0;
}

.detail-table th {
  width: 35%;
  font-weight: 600;
  font-size: 13px;
  color: #718096;
  padding: 10px 8px;
  border: none;
}

.detail-table td {
  font-size: 15px;
  color: #1a202c;
  font-weight: 500;
  padding: 10px 8px;
  border: none;
}

.detail-footer {
  display: flex;
  justify-content: flex-end;
  padding: 16px 24px;
  background: #f7fafc;
  border-top: 1px solid #e2e8f0;
}

.edit-actions {
  display: flex;
  gap: 12px;
}

/* Form fields */
.field-group { margin-bottom: 16px; }
.field-group label {
  font-size: 13px;
  font-weight: 600;
  color: #4a5568;
  margin-bottom: 4px;
  display: block;
}

.field-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.time-range {
  display: flex;
  align-items: center;
  gap: 8px;
}

.range-sep {
  color: #718096;
  font-weight: 600;
}

/* Badges */
.badge-corporate {
  background: #c6f6d5;
  color: #22543d;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 4px;
}

.badge-shift {
  background: #bee3f8;
  color: #2a4365;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 4px;
}

.badge-on {
  background: #c6f6d5;
  color: #22543d;
  font-size: 12px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 4px;
}

.badge-off {
  background: #e2e8f0;
  color: #718096;
  font-size: 12px;
  font-weight: 600;
  padding: 3px 10px;
  border-radius: 4px;
}

/* Pattern description */
.pattern-description {
  margin-top: 16px;
}

.desc-card {
  display: flex;
  gap: 12px;
  padding: 14px 16px;
  background: #f0fff4;
  border: 1px solid #c6f6d5;
  border-radius: 8px;
}

.desc-card.desc-shift {
  background: #ebf8ff;
  border-color: #bee3f8;
}

.desc-icon {
  font-size: 20px;
  color: #049C4C;
  margin-top: 2px;
}

.desc-shift .desc-icon {
  color: #3182ce;
}

.desc-card p {
  margin: 4px 0 0;
  font-size: 13px !important;
  color: #718096;
}

/* Pattern select buttons */
.pattern-select {
  display: flex;
  gap: 10px;
}

.pattern-btn {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 16px;
  border: 2px solid #d2d6dc;
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
  transition: all 0.15s;
  text-align: left;
}

.pattern-btn:hover {
  border-color: #049C4C;
}

.pattern-btn.active {
  border-color: #049C4C;
  background: #f0fff4;
}

.pattern-btn i {
  font-size: 22px;
  color: #718096;
}

.pattern-btn.active i {
  color: #049C4C;
}

.pattern-btn strong {
  display: block;
  font-size: 14px;
  color: #1a202c;
}

.pattern-btn small {
  display: block;
  font-size: 11px;
  color: #718096;
  margin-top: 2px;
}

/* Toggle */
.toggle-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #f7fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.toggle-label {
  font-size: 14px;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
}

.switch {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  margin: 0;
}

.switch input { opacity: 0; width: 0; height: 0; }

.slider {
  position: absolute;
  cursor: pointer;
  top: 0; left: 0; right: 0; bottom: 0;
  background-color: #cbd5e0;
  transition: 0.2s;
  border-radius: 24px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: #fff;
  transition: 0.2s;
  border-radius: 50%;
}

input:checked + .slider { background-color: #049C4C; }
input:checked + .slider:before { transform: translateX(20px); }

/* Templates */
.tmpl-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.tmpl-card {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px 14px;
  margin-bottom: 8px;
  background: #f7fafc;
}

.tmpl-row {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  flex-wrap: wrap;
}

.tmpl-field {
  flex: 0.8;
}

.tmpl-field label {
  font-size: 11px;
  font-weight: 600;
  color: #718096;
  margin-bottom: 2px;
  display: block;
}

.tmpl-check {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 0.5;
}

.tmpl-check input[type="checkbox"] {
  width: 18px;
  height: 18px;
  margin-top: 4px;
}

.tmpl-del {
  height: 31px;
  flex-shrink: 0;
  align-self: flex-end;
}

.shop-image-section {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
  padding: 12px;
  background: #f7fafc;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
}

.shop-image-preview {
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.public-url-box {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-top: 12px;
}

.public-url-box .form-control {
  font-size: 13px;
  background: #f7fafc;
}

.time-adj-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.time-adj-select {
  width: 100px;
}
</style>
